import and from 'ember-truth-helpers/helpers/and';
import not from 'ember-truth-helpers/helpers/not';
import isTrue from 'volta/helpers/is-true';
import or from 'ember-truth-helpers/helpers/or';
import eq from 'ember-truth-helpers/helpers/eq';
import orElse from 'volta/helpers/or-else';
import { concat } from '@ember/helper';
import typeOf from 'volta/helpers/typeof';

import BvInline from 'volta/components/bv-inline';
import BvText from 'volta/components/bv-text';
import BvBtnGroup from 'volta/components/bv-btn-group';
import BvBtn from 'volta/components/bv-btn';

import type { ComponentLike, ContentValue } from '@glint/template';
import type { TOC } from '@ember/component/template-only';
import type { BvBtnArgs } from 'volta/components/bv-btn';

export interface IHeaderAction extends BvBtnArgs {
  visible?: boolean;
  classNames?: string;
}

export interface BvCardHeaderSignature {
  Args: {
    actions?: (IHeaderAction | undefined)[];
    subtitle?: ContentValue | ComponentLike;
    title?: ContentValue;
  };
  Blocks: {
    default: [];
  };
  Element: HTMLDivElement;
}

const BvCardHeaderComponent: TOC<BvCardHeaderSignature> = <template>
  <div class='bv-card__header' ...attributes>
    {{#if (and (not (isTrue @title)) (not @actions) (has-block))}}
      {{yield}}
    {{else if (or (isTrue @title) @actions)}}
      <BvInline @blockAlign='baseline' class='u-full-width'>
        <BvInline @blockAlign='baseline' class='u-flex-1' data-test-card-title>
          {{#if (eq (typeOf @title) 'string')}}
            <BvText @text={{@title}} @variant='heading-md' />
          {{else}}
            {{@title}}
          {{/if}}
          {{#if @subtitle}}
            <div class='u-ml-2' data-test-card-subtitle>
              {{! @glint-expect-error: Rendering ComponentLike in a block is not supported yet }}
              {{@subtitle}}
            </div>
          {{/if}}
        </BvInline>
        {{#if @actions.length}}
          <BvBtnGroup @segmented={{false}}>
            {{#each @actions as |act index|}}
              {{#if (and act (orElse act.visible true))}}
                <BvBtn
                  @size={{orElse act.size 'sm'}}
                  @subtle={{act.subtle}}
                  @plain={{orElse act.plain true}}
                  @color={{act.color}}
                  @outline={{act.outline}}
                  @rounded={{act.rounded}}
                  @iconLeft={{act.iconLeft}}
                  @iconRight={{act.iconRight}}
                  @tooltipText={{act.tooltipText}}
                  @tooltipSide={{act.tooltipSide}}
                  @text={{act.text}}
                  @onClick={{act.onClick}}
                  @disabled={{act.disabled}}
                  @isLoading={{act.isLoading}}
                  class={{act.classNames}}
                  @testName={{concat 'card-header-actions-' index}}
                />
              {{/if}}
            {{/each}}
          </BvBtnGroup>
        {{/if}}
        {{yield}}
      </BvInline>
    {{else}}
      {{#if (eq (typeOf @title) 'string')}}
        <BvText @text={{@title}} @variant='heading-md' />
      {{else}}
        {{@title}}
      {{/if}}
    {{/if}}
  </div>
</template>;

export default BvCardHeaderComponent;

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'BvCard::Header': typeof BvCardHeaderComponent;
    'bv-card/header': typeof BvCardHeaderComponent;
  }
}
